import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'
import API from '@/lib/api'

const schema = z.object({
  webhookUrl: z.union([
    z.string().trim().url('settings.api.webhook.url.errors.invalid'),
    z.literal(''),
  ]),
})

type WebhookFormData = z.infer<typeof schema>

export function WebhookForm({
  defaultValues,
}: {
  defaultValues?: WebhookFormData
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const form = useForm<WebhookFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      webhookUrl: defaultValues?.webhookUrl || '',
    },
  })

  const onSubmit = (data: WebhookFormData) =>
    API.patch('/api/v2/settings', {
      webhookUrl: data.webhookUrl,
    })
      .then(() => {
        toast.success(t('invoicing.generic.success'))
        form.reset(data)
      })
      .catch(() => {
        toast.error('Adresu webhooku se nepodařilo uložit.')
      })

  const events = [
    { code: 'invoiceUpdated', label: t('api.webhook.url.events.invoiceUpdated') },
    { code: 'invoicePaid', label: t('api.webhook.url.events.invoicePaid') },
    { code: 'orderPaid', label: t('api.webhook.url.events.orderPaid') },
  ]

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <FormField
          control={form.control}
          name="webhookUrl"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('api.webhook.url.label')}</FormLabel>
              <FormControl>
                <InputGroup>
                  <InputGroupInput {...field} type="url" placeholder="https://example.com/webhook" />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton variant="default" type="submit" disabled={form.formState.isSubmitting}>
                      {tg('button.save')}
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
              </FormControl>
              <FormMessage>{fieldState.error ? t(fieldState.error.message as string) : ''}</FormMessage>
              <FormDescription>Nechte prázdné, pokud webhooky nepoužíváte.</FormDescription>
            </FormItem>
          )}
        />

        <div className="overflow-hidden rounded-lg border">
          <div className="grid h-9 grid-cols-[180px_1fr] items-center border-b px-4 text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
            <span>Událost</span>
            <span>Kdy se pošle</span>
          </div>
          <dl className="divide-y">
            {events.map((event) => (
              <div key={event.code} className="grid min-h-11 grid-cols-[180px_1fr] items-center px-4 text-sm">
                <dt>
                  <code className="font-mono text-[13px]">{event.code}</code>
                </dt>
                <dd className="text-muted-foreground">{event.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </form>
    </Form>
  )
}
